import { ApiResponse } from '@/types';
import api from './api';
import { PROFILES_API_ENDPOINTS } from '@/constants';
import { IRegisterPhotoResponse } from '@/interfaces';

// Upload a complete photo file in chunks
export const PhotoUploadService = {
  uploadPhotoInChunks: async (
    file: File,
    onProgress?: (progress: number) => void,
  ): Promise<string> => {
    // Register the upload and get chunk information
    const { fileId, chunks } = await registerPhotoUpload(file.name, file.size);

    // Read the file as ArrayBuffer
    const arrayBuffer = await file.arrayBuffer();
    const fileData = new Uint8Array(arrayBuffer);

    const totalChunks = chunks.length;

    // Upload each chunk using the provided chunk information
    for (let i = 0; i < totalChunks; i++) {
      const chunkInfo = chunks[i];
      const chunk = fileData.slice(chunkInfo.startByte, chunkInfo.endByte + 1);

      await uploadPhotoChunk(fileId, chunkInfo.chunkIndex, chunk);

      // Report progress
      if (onProgress) {
        const progress = Math.round(((i + 1) / totalChunks) * 100);
        onProgress(progress);
      }
    }

    return fileId;
  },
};

// Register a photo upload and get the chunk size
const registerPhotoUpload = async (
  fileName: string,
  sizeInBytes: number,
): Promise<IRegisterPhotoResponse> => {
  const response = await api.post<ApiResponse<IRegisterPhotoResponse>>(
    PROFILES_API_ENDPOINTS.PROFILES_PHOTO_CHUNKS,
    { fileName, sizeInBytes },
  );
  return response.data.data;
};

// Upload a single chunk of the photo
const uploadPhotoChunk = async (
  photoId: string,
  chunkIndex: number,
  chunkData: Uint8Array,
): Promise<void> => {
  const endpoint = PROFILES_API_ENDPOINTS.PROFILES_PHOTO_CHUNKS_INDEX.replace(
    ':photoId',
    photoId,
  ).replace(':chunkIndex', chunkIndex.toString());

  const formData = new FormData();
  const blob = new Blob([chunkData.buffer as ArrayBuffer], { type: 'application/octet-stream' });
  formData.append('chunk', blob);

  await api.put(endpoint, formData, {
    headers: {
      'Content-Type': 'multipart/form-data',
    },
  });
};

export default PhotoUploadService;
